import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { TextAreaField } from "@/components/Field";
import { EmptyState } from "@/components/EmptyState";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { useGroups } from "@/lib/hooks/usePeople";
import { call, CALLABLES } from "@/services/callables";
import { reachableStorageUrl } from "@/services/firebase";
import { failureKey, toAppError } from "@/services/errors";
import { formatDate, formatDateTime } from "@/lib/format";
import { legendKey, frameworkKey, presetForScore, SCORE_LEVELS } from "./assessment";
import { deletionDate, retentionState } from "./retention";
import {
  beatView,
  isLiveRun,
  isScriptedRun,
  lessonKind,
  lessonKindKey,
  roleName,
  scenesToLoad,
  type BeatView,
} from "./beats";
import { beatMarks, driesByActor, hasDries } from "./dries";
import { formatSeconds, partnerMatrix, rounds } from "./rounds";
import { acts, draws } from "./acts";
import {
  NOTES_MAX_LENGTH,
  type Cue,
  type Run,
  type Scenario,
  type ScoreLevel,
  type SessionEvent,
  type SessionReview,
  type SessionScore,
} from "@/types";

/* Glyphs, not words: the legend under the marks says what each one means, in the reader's language. */
const PROMPTED_MARK = "\u25CF";
const ASKED_MARK = "\u25CB";

/**
 * One lesson, reviewed.
 *
 * **Teacher-only, and refused by the database rather than hidden by this
 * page.** An owner who opens it is told whose it is, which is a better answer
 * than an empty screen and a truer one than a 404.
 */

type Tab = "overview" | "runs" | "room" | "show" | "scores" | "notes";

/**
 * The tabs a lesson has.
 *
 * Room and Show appear only when there is something to show in them: a room
 * lesson or a show night always has its own, and any lesson that recorded a
 * round, an act or a draw gets the tab that reads it. A lesson of scenes
 * keeps the four tabs it always had.
 */
function tabsFor(review: SessionReview): readonly { id: Tab; labelKey: string }[] {
  const kind = lessonKind(review.session);
  const events = review.events ?? [];
  const tabs: { id: Tab; labelKey: string }[] = [
    { id: "overview", labelKey: "review.tab.overview" },
    { id: "runs", labelKey: "review.tab.runs" },
  ];
  if (kind === "room" || rounds(events).length > 0) {
    tabs.push({ id: "room", labelKey: "review.tab.room" });
  }
  if (kind === "show" || acts(events).length > 0 || draws(events).length > 0) {
    tabs.push({ id: "show", labelKey: "review.tab.show" });
  }
  tabs.push({ id: "scores", labelKey: "review.tab.scores" }, { id: "notes", labelKey: "review.tab.notes" });
  return tabs;
}

export function SessionReviewPage() {
  const { sessionId = "" } = useParams();
  const t = useT();
  const { locale } = useI18n();
  const { institution } = useAuth();
  const { groups } = useGroups();
  const [tab, setTab] = useState<Tab>("overview");

  const review = useCallable<SessionReview, { sessionId: string }>(
    CALLABLES.getSessionReview,
    { sessionId },
    sessionId.length > 0
  );

  const groupName = useMemo(
    () => groups.find((group) => group.id === review.data?.session.groupId)?.name ?? "",
    [groups, review.data]
  );

  if (review.loading) {
    return (
      <SectionShell titleKey="sessions.title">
        <p className="text-sm text-ink-muted">{t("common.loading")}</p>
      </SectionShell>
    );
  }

  if (!review.data) {
    /*
      Three different failures, three different sentences. The page used to
      answer every one of them with "Session detail is visible to the teaching
      teacher" — including a lesson id that does not exist, which is not a
      permission question at all (live run, 2026-09-09).
    */
    const code = review.error?.code;
    const notFound = code === "not-found";
    const refused = code === "permission-denied";

    return (
      <SectionShell titleKey="sessions.title">
        {notFound ? (
          <>
            <h2 className="section-title">{t("review.notFound")}</h2>
            <p className="mt-2 max-w-2xl text-ink-soft">{t("review.notFoundBody")}</p>
          </>
        ) : (
          <p className="max-w-2xl text-ink-soft">
            {refused ? t("review.teacherOnly") : t(failureKey(review.error))}
          </p>
        )}

        {/* A dead end had no way out of it but the browser's Back button. */}
        <Link
          to="/sessions"
          className="mt-4 inline-block text-sm font-medium text-brand underline underline-offset-2 hover:text-brand-hover"
        >
          {t("review.allLessons")}
        </Link>
      </SectionShell>
    );
  }

  const { session } = review.data;
  const operationalDays = institution?.retention.operationalDays ?? 365;
  const state = retentionState(session.startedAt, operationalDays, Date.now());
  const deletes = deletionDate(session.startedAt, operationalDays);

  return (
    <SectionShell
      titleKey="review.title"
      titleParams={{ group: groupName, date: formatDate(locale, session.startedAt) }}
    >
      {state !== "distant" && deletes ? (
        <p
          className={`mb-6 rounded-card border px-5 py-4 text-sm ${
            state === "gone"
              ? "border-line bg-surface-muted text-ink-soft"
              : "border-warning bg-warning-soft text-ink-soft"
          }`}
        >
          {t(state === "gone" ? "review.retentionGone" : "review.retentionSoon", {
            date: formatDate(locale, deletes.toISOString()),
          })}
        </p>
      ) : null}

      <div role="tablist" className="flex flex-wrap gap-1 border-b border-line">
        {tabsFor(review.data).map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={entry.id === tab}
            onClick={() => setTab(entry.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm transition-colors ${
              entry.id === tab
                ? "border-brand font-medium text-ink"
                : "border-transparent text-ink-muted hover:text-ink"
            }`}
          >
            {t(entry.labelKey)}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "overview" ? <Overview review={review.data} /> : null}
        {tab === "runs" ? <Runs review={review.data} /> : null}
        {tab === "room" ? <Room review={review.data} /> : null}
        {tab === "show" ? <Show review={review.data} /> : null}
        {tab === "scores" ? <Scores review={review.data} onSaved={review.reload} /> : null}
        {tab === "notes" ? (
          <Notes review={review.data} operationalDays={operationalDays} />
        ) : null}
      </div>
    </SectionShell>
  );
}

function Overview({ review }: { review: SessionReview }) {
  const t = useT();
  const { locale } = useI18n();
  const { session } = review;
  const kind = lessonKind(session);

  return (
    <div className="max-w-2xl">
      <dl className="grid grid-cols-[10rem_1fr] gap-y-2 text-sm">
        <dt className="text-ink-muted">{t("sessions.column.date")}</dt>
        <dd className="text-ink">{formatDateTime(locale, session.startedAt)}</dd>
        {kind ? (
          <>
            <dt className="text-ink-muted">{t("review.lesson")}</dt>
            <dd className="text-ink">{t(lessonKindKey(kind))}</dd>
          </>
        ) : null}
        <dt className="text-ink-muted">{t("sessions.column.mode")}</dt>
        <dd className="text-ink">{t(`institution.mode.${session.mode}`)}</dd>
        <dt className="text-ink-muted">{t("sessions.column.runs")}</dt>
        <dd className="text-ink">{review.runs.length}</dd>
      </dl>

      <h2 className="section-title mt-8">{t("review.register")}</h2>
      <ul className="mt-3 flex flex-col gap-1.5 text-sm">
        {session.register.map((entry) => (
          <li key={entry.uid} className="flex items-center justify-between gap-4">
            <span className="text-ink">{entry.displayName}</span>
            <span className={entry.present ? "text-positive" : "text-ink-faint"}>
              {t(entry.present ? "review.present" : "review.absent")}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/*
  Names, not identifiers.

  A run stores the scene's id and version and each seat's role id — the record
  is about what was played, not what it was called. Until 2026-10-01 the page
  printed those ids ("LXFdqZ…, version 1"; "a", "b", "c") and never said who held
  a part. The scene's title and role names come from the version that was played,
  and a player's name from this lesson's register.

  A live run has no scene to load: it is called a live scene, and its roles
  ("Player 1"…) are on the run itself.
*/
function useScenesPlayed(runs: SessionReview["runs"]) {
  const [scenes, setScenes] = useState<Record<string, Scenario>>({});
  const wanted = useMemo(() => scenesToLoad(runs), [runs]);

  useEffect(() => {
    let cancelled = false;
    for (const key of wanted) {
      const [scenarioId, version] = key.split("@");
      call<{ scenarioId: string; version: number }, { scenario: Scenario }>(
        CALLABLES.getScenarioVersion,
        { scenarioId: scenarioId!, version: Number(version) },
      )
        .then(({ scenario }) => {
          if (!cancelled) setScenes((current) => ({ ...current, [key]: scenario }));
        })
        // A scene that can no longer be read falls back to its id, as before.
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
    };
  }, [wanted]);

  return scenes;
}

function useNames(review: SessionReview) {
  return (uid: string) =>
    review.session.register.find((entry) => entry.uid === uid)?.displayName ?? uid;
}

function Runs({ review }: { review: SessionReview }) {
  const t = useT();
  const scenes = useScenesPlayed(review.runs);
  const nameOf = useNames(review);
  const events = review.events ?? [];

  if (review.runs.length === 0) return <EmptyState titleKey="review.noRuns" />;

  return (
    <ol className="flex max-w-3xl flex-col gap-6">
      {review.runs.map((run, index) => {
        const scene = scenes[`${run.scenarioId}@${run.scenarioVersion}`];
        return (
        <li key={run.id} className="card p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h2 className="section-title">
              {t("review.run", { number: index + 1 })}
            </h2>
            <span className="text-sm text-ink-muted">
              {isLiveRun(run)
                ? t("review.liveScene")
                : t("review.runScenario", {
                    title: scene?.title ?? run.scenarioId,
                    version: run.scenarioVersion,
                  })}
              {run.aborted ? (
                <span className="ml-2 text-danger">{t("review.runAborted")}</span>
              ) : null}
            </span>
          </div>

          <h3 className="mt-4 field-label">{t("review.cast")}</h3>
          <ul className="flex flex-col gap-1 text-sm">
            {run.cast.map((assignment) => (
              <li key={assignment.uid} className="text-ink-soft">
                <span className="text-ink">{nameOf(assignment.uid)}</span>
                <span className="ml-1">
                  {t("review.castAs", { role: roleName(run, scene, assignment.roleId) })}
                </span>
                {assignment.difficulty ? (
                  <span className="ml-2 text-ink-faint">
                    {t("review.difficulty", { level: assignment.difficulty })}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>

          <h3 className="mt-4 field-label">{t("review.timeline")}</h3>
          <ol className="flex flex-col gap-2 text-sm text-ink-soft">
            {run.cues.map((cue, position) => (
              <li key={cue.id} className="flex gap-2">
                <span className="w-5 shrink-0 text-ink-faint">{position + 1}</span>
                <span className="w-24 shrink-0 text-ink-muted">
                  {roleName(run, scene, cue.roleId)}
                </span>
                <Beat cue={cue} />
              </li>
            ))}
          </ol>

          {/*
            A rehearsal's record: where each actor needed a line. Shown for a
            script, and for any run that recorded a dry — a scene of
            situations with a prompt in it is still worth reading.
          */}
          {isScriptedRun(run) || hasDries(run, events) ? (
            <DryMap run={run} scene={scene} events={events} nameOf={nameOf} />
          ) : null}
        </li>
        );
      })}
    </ol>
  );
}

/**
 * The dry map of one run.
 *
 * Per actor: each beat they needed a line on, with the line itself from the
 * run's own copy of the script and what was sent. Then one mark per beat of
 * the script, so a teacher sees at a glance where the scene is thin. Counts
 * and places; never a score.
 */
function DryMap({
  run,
  scene,
  events,
  nameOf,
}: {
  run: Run;
  scene: Scenario | undefined;
  events: readonly SessionEvent[];
  nameOf: (uid: string) => string;
}) {
  const t = useT();
  const actors = driesByActor(run, events);
  const marks = beatMarks(run, events);

  return (
    <section aria-label={t("review.dries.title")} className="mt-5 border-t border-line pt-4">
      <h3 className="field-label">{t("review.dries.title")}</h3>
      <p className="text-sm text-ink-muted">{t("review.dries.intro")}</p>

      <ul className="mt-3 flex flex-col gap-3 text-sm">
        {actors.map((actor) => (
          <li key={actor.uid}>
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-ink">{nameOf(actor.uid)}</span>
              <span className="text-ink-muted">
                {t("review.castAs", { role: roleName(run, scene, actor.roleId) })}
              </span>
              <span className="text-ink-soft">
                {actor.dries.length === 0
                  ? t("review.dries.none")
                  : t("review.dries.summary", {
                      count: actor.dries.length,
                      prompted: actor.prompted,
                      asked: actor.asked,
                    })}
              </span>
            </div>
            {actor.dries.length > 0 ? (
              <ol className="mt-1 flex flex-col gap-1 pl-4 text-ink-soft">
                {actor.dries.map((dry, index) => (
                  <li key={`${dry.cueId ?? "off"}-${index}`} className="flex flex-wrap gap-x-2">
                    <span className="text-ink-muted">
                      {dry.beat !== undefined
                        ? t("review.dries.beat", { number: dry.beat })
                        : t("review.dries.offScript")}
                    </span>
                    {dry.line ? <span>{t("review.beat.line", { text: dry.line })}</span> : null}
                    {/* One beat, whatever happened on it: asked, then what was sent. */}
                    {dry.asked ? (
                      <span className="text-ink-faint">{t("review.dries.asked")}</span>
                    ) : null}
                    {dry.prompted && dry.prompts.length === 0 ? (
                      <span className="text-ink-faint">{t("review.dries.promptedLine")}</span>
                    ) : null}
                    {dry.prompts.map((sent, position) => (
                      <span key={position} className="text-ink-faint">
                        {t("review.dries.prompted", { text: sent })}
                      </span>
                    ))}
                  </li>
                ))}
              </ol>
            ) : null}
          </li>
        ))}
      </ul>

      <h4 className="mt-4 field-label">{t("review.dries.marks")}</h4>
      <ol className="flex flex-wrap gap-1" aria-label={t("review.dries.marks")}>
        {marks.map((mark) => {
          const what = !mark.dried
            ? "clean"
            : mark.prompted && mark.asked
              ? "both"
              : mark.prompted
                ? "prompted"
                : "asked";
          return (
            <li
              key={mark.cueId}
              aria-label={t(`review.dries.mark.${what}`, {
                number: mark.beat,
                role: roleName(run, scene, mark.roleId),
              })}
              className={`flex h-8 min-w-8 items-center justify-center gap-0.5 rounded-control border px-1.5 text-xs ${
                mark.dried ? "border-brand bg-brand-soft text-ink" : "border-line text-ink-faint"
              }`}
            >
              <span>{mark.beat}</span>
              {mark.prompted ? <span aria-hidden="true">{PROMPTED_MARK}</span> : null}
              {mark.asked ? <span aria-hidden="true">{ASKED_MARK}</span> : null}
            </li>
          );
        })}
      </ol>
      <p className="mt-1 text-xs text-ink-faint">{t("review.dries.legend")}</p>
    </section>
  );
}

const CELL = "h-10 px-3 py-2.5";
const HEAD = "h-9 px-3 text-[13px] font-medium";

/**
 * A room lesson: the rounds, then who worked with whom.
 *
 * Replaces the clipboard. The matrix is read in the register's order, and
 * the last column says how often each student was up at all — the fairness
 * the room mode exists for.
 */
function Room({ review }: { review: SessionReview }) {
  const t = useT();
  const nameOf = useNames(review);
  const played = rounds(review.events ?? []);
  const matrix = partnerMatrix(
    played,
    review.session.register.map((entry) => entry.uid)
  );

  if (played.length === 0) return <EmptyState titleKey="review.rounds.empty" />;

  return (
    <div className="max-w-3xl">
      <h2 className="section-title">{t("review.rounds.title")}</h2>
      <p className="mt-1 text-sm text-ink-muted">{t("review.rounds.intro")}</p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full border-collapse text-[14px]">
          <caption className="sr-only">{t("review.rounds.title")}</caption>
          <thead>
            <tr className="border-b border-line bg-surface-muted text-left text-ink-muted">
              <th scope="col" className={HEAD}>{t("review.rounds.column.exercise")}</th>
              <th scope="col" className={HEAD}>{t("review.rounds.column.who")}</th>
              <th scope="col" className={HEAD}>{t("review.rounds.column.length")}</th>
            </tr>
          </thead>
          <tbody>
            {played.map((round, index) => (
              <tr key={round.id} className="border-b border-line last:border-0">
                <td className={`${CELL} text-ink`}>
                  <span className="mr-2 text-ink-faint">{t("review.rounds.round", { number: index + 1 })}</span>
                  {round.exercise || t("review.rounds.untitled")}
                </td>
                <td className={`${CELL} text-ink-muted`}>{round.uids.map(nameOf).join(", ")}</td>
                <td className={`${CELL} text-ink-muted`}>
                  {round.seconds !== undefined ? formatSeconds(round.seconds) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="section-title mt-8">{t("review.matrix.title")}</h2>
      <p className="mt-1 text-sm text-ink-muted">{t("review.matrix.intro")}</p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full border-collapse text-[14px]">
          <caption className="sr-only">{t("review.matrix.title")}</caption>
          <thead>
            <tr className="border-b border-line bg-surface-muted text-left text-ink-muted">
              <th scope="col" className={HEAD}>
                <span className="sr-only">{t("review.rounds.column.who")}</span>
              </th>
              {matrix.uids.map((uid) => (
                <th key={uid} scope="col" className={`${HEAD} text-center`}>
                  {nameOf(uid)}
                </th>
              ))}
              <th scope="col" className={`${HEAD} text-center`}>{t("review.matrix.timesUp")}</th>
            </tr>
          </thead>
          <tbody>
            {matrix.uids.map((row) => (
              <tr key={row} className="border-b border-line last:border-0">
                <th scope="row" className={`${CELL} text-left font-normal text-ink`}>
                  {nameOf(row)}
                </th>
                {matrix.uids.map((column) => (
                  <td
                    key={column}
                    className={`${CELL} text-center ${row === column ? "text-ink-faint" : "text-ink-muted"}`}
                    aria-label={
                      row === column
                        ? undefined
                        : t("review.matrix.cell", {
                            a: nameOf(row),
                            b: nameOf(column),
                            count: matrix.together(row, column),
                          })
                    }
                  >
                    {row === column ? "·" : matrix.together(row, column)}
                  </td>
                ))}
                <td className={`${CELL} text-center font-medium text-ink`}>{matrix.timesUp(row)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** A show night: the acts as they played, and the names that were drawn. */
function Show({ review }: { review: SessionReview }) {
  const t = useT();
  const nameOf = useNames(review);
  const events = review.events ?? [];
  const played = acts(events);
  const drawn = draws(events);

  return (
    <div className="max-w-3xl">
      <h2 className="section-title">{t("review.acts.title")}</h2>
      <p className="mt-1 text-sm text-ink-muted">{t("review.acts.intro")}</p>
      {played.length === 0 ? (
        <p className="mt-3 text-sm text-ink-soft">{t("review.acts.empty")}</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse text-[14px]">
            <caption className="sr-only">{t("review.acts.title")}</caption>
            <thead>
              <tr className="border-b border-line bg-surface-muted text-left text-ink-muted">
                <th scope="col" className={HEAD}>{t("review.acts.column.title")}</th>
                <th scope="col" className={HEAD}>{t("review.acts.column.performers")}</th>
                <th scope="col" className={HEAD}>{t("review.acts.column.length")}</th>
              </tr>
            </thead>
            <tbody>
              {played.map((act, index) => (
                <tr key={act.id} className="border-b border-line last:border-0">
                  <td className={`${CELL} text-ink`}>
                    <span className="mr-2 text-ink-faint">{t("review.acts.act", { number: index + 1 })}</span>
                    {act.title || t("review.acts.untitled")}
                  </td>
                  <td className={`${CELL} text-ink-muted`}>{act.uids.map(nameOf).join(", ")}</td>
                  <td className={`${CELL} text-ink-muted`}>
                    {act.seconds !== undefined ? formatSeconds(act.seconds) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="section-title mt-8">{t("review.jam.title")}</h2>
      {drawn.length === 0 ? (
        <p className="mt-3 text-sm text-ink-soft">{t("review.jam.empty")}</p>
      ) : (
        <ol className="mt-3 flex flex-col gap-3 text-sm">
          {drawn.map((draw, index) => (
            <li key={draw.id} className="card p-4">
              <h3 className="field-label">{t("review.jam.draw", { number: index + 1 })}</h3>
              <ul className="flex flex-col gap-1">
                {draw.groups.map((group) => (
                  <li key={group.number} className="flex flex-wrap gap-x-2">
                    <span className="text-ink-muted">{t("review.jam.group", { number: group.number })}</span>
                    <span className="text-ink">
                      {[...group.uids.map(nameOf), ...group.names].join(", ")}
                    </span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/**
 * One beat, the way its kind reads: a situation as written, a line in
 * quotation marks, a picture as a thumbnail with its caption.
 */
function Beat({ cue }: { cue: Cue }) {
  const t = useT();
  const view: BeatView = beatView(cue);
  const feeling = view.feeling ? (
    <span className="ml-2 text-ink-faint">{view.feeling}</span>
  ) : null;

  if (view.kind === "image") {
    return (
      <span className="flex items-start gap-3">
        <BeatPicture path={view.path} alt={view.caption || t("review.beat.picture")} />
        <span>
          {view.caption ? <span className="text-ink-soft">{view.caption}</span> : null}
          {feeling}
        </span>
      </span>
    );
  }
  return (
    <span>
      {view.kind === "line" ? t("review.beat.line", { text: view.text }) : view.text}
      {feeling}
    </span>
  );
}

/**
 * A picture from Storage, small.
 *
 * Read with the teacher's own sign-in (Storage rules let staff read pictures);
 * a picture that cannot be read leaves a quiet placeholder rather than a broken
 * image, because the rest of the timeline is still worth reading.
 */
function BeatPicture({ path, alt }: { path: string; alt: string }) {
  const t = useT();
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const sha256 = /scene-images\/([0-9a-f]{64})\.jpg$/.exec(path)?.[1];
    /*
      Through the backend, which checks the role and hands back a download
      link. Reading Storage directly depended on a cross-service rule that
      production refused (2026-10-05), and every thumbnail failed.
    */
    (sha256
      ? call<{ sha256s: string[] }, { urls: Record<string, string> }>(CALLABLES.getSceneImageUrls, {
          sha256s: [sha256],
        }).then(({ urls }) => {
          const found = urls[sha256];
          if (!found) throw new Error("[review] no link for the picture");
          return reachableStorageUrl(found);
        })
      : Promise.reject(new Error("[review] not a scene picture")))
      .then((found) => {
        if (!cancelled) setUrl(found);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  const box = "h-[39px] w-[52px] shrink-0 rounded-control border border-line";
  if (url) return <img src={url} alt={alt} className={`${box} object-cover`} />;
  return (
    <span
      role="img"
      aria-label={failed ? t("review.beat.pictureMissing") : alt}
      className={`${box} bg-surface-muted`}
    />
  );
}

function Scores({ review, onSaved }: { review: SessionReview; onSaved: () => void }) {
  const t = useT();
  const { institution } = useAuth();
  const { success, showError } = useToast();
  const [busy, setBusy] = useState(false);

  const preset = institution?.assessmentPreset ?? "custom";
  const scoreFor = (runId: string, uid: string): SessionScore | undefined =>
    review.scores.find((score) => score.runId === runId && score.uid === uid);

  const nameOf = (uid: string) =>
    review.session.register.find((entry) => entry.uid === uid)?.displayName ?? uid;

  const setScore = async (run: Run, uid: string, level: ScoreLevel | undefined) => {
    setBusy(true);
    try {
      if (level === undefined) {
        await call(CALLABLES.clearSessionScore, {
          sessionId: review.session.id,
          runId: run.id,
          uid,
        });
      } else {
        await call(CALLABLES.setSessionScore, {
          sessionId: review.session.id,
          runId: run.id,
          uid,
          level,
        });
      }
      success("review.scores.saved");
      onSaved();
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  if (review.runs.length === 0) return <EmptyState titleKey="review.noRuns" />;

  return (
    <div className="max-w-3xl">
      {/* Optional, and it stays optional. */}
      <p className="text-sm text-ink-soft">{t("review.scores.intro")}</p>
      <p className="mt-1 text-sm text-ink-muted">
        {t("review.scores.framework", { name: t(frameworkKey(preset)) })}
      </p>

      <ul className="mt-4 flex flex-col gap-1 text-sm text-ink-muted">
        {SCORE_LEVELS.map((level) => (
          <li key={level}>
            <span className="mr-2 font-medium text-ink">{level}</span>
            {t(legendKey(preset, level))}
          </li>
        ))}
      </ul>

      <div className="mt-8 flex flex-col gap-6">
        {review.runs.map((run, index) => (
          <section key={run.id} className="card p-5" aria-label={t("review.run", { number: index + 1 })}>
            <h2 className="section-title">
              {t("review.run", { number: index + 1 })}
            </h2>

            <ul className="mt-4 flex flex-col gap-3">
              {run.cast.map((assignment) => {
                const score = scoreFor(run.id, assignment.uid);
                const shownPreset = presetForScore(score, preset);

                return (
                  <li
                    key={assignment.uid}
                    className="flex flex-wrap items-center justify-between gap-3"
                  >
                    <span className="text-sm text-ink">{nameOf(assignment.uid)}</span>

                    <div className="flex items-center gap-1">
                      {SCORE_LEVELS.map((level) => (
                        <button
                          key={level}
                          type="button"
                          disabled={busy}
                          aria-pressed={score?.level === level}
                          aria-label={`${nameOf(assignment.uid)} ${level}`}
                          title={t(legendKey(shownPreset, level))}
                          onClick={() => setScore(run, assignment.uid, level)}
                          className={`h-9 w-9 rounded-control border text-[14px] transition-colors ${
                            score?.level === level
                              ? "border-brand bg-brand text-white"
                              : "border-line bg-white text-ink-soft hover:border-ink-faint"
                          }`}
                        >
                          {level}
                        </button>
                      ))}

                      {score ? (
                        <Button
                          variant="ghost"
                          busy={busy}
                          onClick={() => setScore(run, assignment.uid, undefined)}
                        >
                          {t("review.scores.clear")}
                        </Button>
                      ) : (
                        <span className="ml-2 text-xs text-ink-faint">
                          {t("review.scores.none")}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

const NOTES_DEBOUNCE_MS = 900;

function Notes({
  review,
  operationalDays,
}: {
  review: SessionReview;
  operationalDays: number;
}) {
  const t = useT();
  const { locale } = useI18n();
  const { showError } = useToast();
  const [text, setText] = useState(review.notes?.text ?? "");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | undefined>(undefined);
  const timer = useRef<number | undefined>(undefined);
  const dirty = useRef(false);

  const deletes = deletionDate(review.session.startedAt, operationalDays);

  useEffect(() => {
    if (!dirty.current) return;

    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      setSaving(true);
      try {
        await call(CALLABLES.saveSessionNotes, {
          sessionId: review.session.id,
          text,
        });
        setSavedAt(Date.now());
      } catch (error) {
        showError(toAppError(error));
      } finally {
        setSaving(false);
      }
    }, NOTES_DEBOUNCE_MS);

    return () => window.clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <div className="max-w-2xl">
      <TextAreaField
        labelKey="review.notes.label"
        className="min-h-64"
        maxLength={NOTES_MAX_LENGTH}
        value={text}
        onChange={(event) => {
          dirty.current = true;
          setText(event.target.value);
        }}
      />

      {/*
        The retention window, stated where the writing happens. A teacher who
        loses last term's notes without warning has been surprised by their own
        tool, and a policy page is not a warning.
      */}
      {deletes ? (
        <p className="mt-2 text-sm text-ink-muted">
          {t("review.notes.retention", { date: formatDate(locale, deletes.toISOString()) })}
        </p>
      ) : null}

      <p className="mt-2 text-sm text-ink-muted">
        {saving ? t("review.notes.saving") : savedAt ? t("review.notes.saved") : null}
      </p>
    </div>
  );
}
